import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import classname from "classnames";
import Youtube, { YouTubeEvent } from "react-youtube";

import { useClickOutside } from "@hooks";

import { ItemType } from "../types";

import { SLIDE_TRANSITION_DURATION_MS } from "../constants";
import { Loader } from ".";

interface Props {
  data: ItemType;
  swiperContainerRef: MutableRefObject<HTMLDivElement>;
  isSlideActive: boolean;
  shouldPlayOnLoad: boolean;
  isLighthboxOpen: boolean;
  setActiveContentRef: (ref: MutableRefObject<HTMLDivElement>) => void;
}

const BLUR_TIMEOUT = 3000;

const VideoItem: React.FC<Props> = ({
  data,
  swiperContainerRef,
  isLighthboxOpen,
  shouldPlayOnLoad,
  isSlideActive,
  setActiveContentRef,
}) => {
  const { src, description } = data;

  const [videoEvent, setVideoEvent] = useState<YouTubeEvent>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Focus swiper container on click outside of video
   * to allow window catch keyboard events (navigation || closing of swiper)
   */
  const focusSwiperContainer = (): void => {
    if (!swiperContainerRef?.current) return;

    swiperContainerRef.current.focus();
  };

  /**
   * Blur on click outside of video wrapper to make keyboard events
   * catchable by Swiper instead of youtube iframe
   */
  useClickOutside({
    ref: videoWrapperRef,
    onClickOutsideCb: focusSwiperContainer,
    shouldHandleClick: isLighthboxOpen && isSlideActive,
  });

  const youtubeClasses = classname("lightbox__slide-item-video", {
    "lightbox__slide-item--loading": !videoEvent,
  });

  const pauseVideo = (): void => {
    /**
     * -1 = Unstarted
     *  0 = Ended
     *  1 = Playing
     *  2 = Paused
     *  3 = Buffering
     *  5 = Video cued
     */
    const playerState = videoEvent.target.getPlayerState();

    if (playerState !== 1) return;

    videoEvent.target.pauseVideo();
  };

  // swiper duplicates slides when has continuous loop mode
  // via this function we pause duplicated slide's video
  const handlePause = (): void => {
    if (!videoEvent?.target?.i || !swiperContainerRef?.current) return;

    const duplicatedSlides: NodeListOf<HTMLDivElement> =
      swiperContainerRef.current.querySelectorAll('[class*="duplicate"]');

    if (!duplicatedSlides?.length) {
      return;
    }

    duplicatedSlides.forEach((slide) => {
      const iframe = slide.querySelector("iframe");
      const youtubeOrigin = "https://www.youtube.com"; // Explicitly define the target origin

      if (iframe?.contentWindow && iframe.src.startsWith(youtubeOrigin)) {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          youtubeOrigin
        );
      }
    });
  };

  /**
   * Listen for slider change and pause video
   */
  useEffect(() => {
    if (!videoEvent?.target) return;

    pauseVideo();
  }, [videoEvent]);

  /**
   * Play video when lightbox is opened with video thumbnail
   */
  useEffect(() => {
    if (!videoEvent?.target || !isLighthboxOpen || !shouldPlayOnLoad) return;

    /* Wait while lightbox is fully open */
    window.setTimeout(() => {
      videoEvent.target.playVideo();
    }, SLIDE_TRANSITION_DURATION_MS);
  }, [videoEvent, isLighthboxOpen, shouldPlayOnLoad]);

  /**
   * Set tabindex of iframe based on video position in slider
   */
  useEffect(() => {
    if (!videoEvent?.target) return;

    const iframeEl: HTMLIFrameElement = videoEvent.target.i;

    if (!isLighthboxOpen) {
      iframeEl.tabIndex = -1;
      return;
    }

    iframeEl.tabIndex = 0;
  }, [videoEvent, isLighthboxOpen]);

  /**
   * Pause video on lightbox close
   */
  useEffect(() => {
    if (!videoEvent?.target || isLighthboxOpen) return;

    pauseVideo();
  }, [isLighthboxOpen, videoEvent, isSlideActive]);

  /**
   * Set tabindex of iframe based on active slide state in swiper
   */
  useEffect(() => {
    if (!videoEvent?.target) return;

    const iframeEl: HTMLIFrameElement = videoEvent.target.i;

    if (!isLighthboxOpen || !isSlideActive) {
      iframeEl.tabIndex = -1;

      /**
       * Remove focus from video iframe after slide is not active
       * to prevent keyboard keyboard handling by youtube video
       * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/blur
       */
      setTimeout(() => {
        iframeEl.blur();
      }, BLUR_TIMEOUT);

      /**
       * Pause video on slide change
       */
      pauseVideo();

      return;
    }

    iframeEl.tabIndex = 0;
  }, [videoEvent, isSlideActive, isLighthboxOpen]);

  useEffect(() => {
    if (!videoWrapperRef.current || !isSlideActive) return;

    setActiveContentRef(videoWrapperRef);
  }, [isSlideActive, videoWrapperRef]);

  return (
    <div ref={videoWrapperRef} className="h-full w-full min-w-[8rem]">
      {!videoEvent && <Loader />}

      <Youtube
        className={youtubeClasses}
        iframeClassName="lightbox__slide-item-video-iframe"
        videoId={src}
        title={description}
        onReady={(event) => setVideoEvent(event)}
        opts={{
          height: "100%",
          width: "100%",
        }}
        onPause={handlePause}
      />
    </div>
  );
};

export default VideoItem;
